"use client";

import type {
  CSSProperties,
} from "react";
import type {
  SiteBuilderDesign,
} from "@webdo/site-templates/builder";
import type {
  BuilderStudioThemeInspectorContext,
} from "./types";
import {
  builderSelectOptions,
} from "./builderSelectOptions";

export type BuilderThemePalette = {
  colors: NonNullable<SiteBuilderDesign["colors"]>;
  key: string;
};

export type BuilderThemeComponentField = {
  key: string;
  options: readonly string[];
};

export type BuilderAdvancedThemeInspectorLabels = {
  fieldLabel: (section: string, fieldKey: string) => string;
  optionLabel: (
    section: string,
    fieldKey: string,
    value: string,
  ) => string;
  paletteDescription: string;
  paletteLabel: (paletteKey: string) => string;
  paletteTitle: string;
  sectionLabel: (section: string) => string;
};

export type BuilderAdvancedThemeInspectorConfig = {
  componentFields: readonly BuilderThemeComponentField[];
  fontOptions: readonly string[];
  palettes: readonly BuilderThemePalette[];
  sectionSpacingOptions: readonly string[];
};

const colorKeys = [
  "primary",
  "accent",
  "background",
  "surface",
  "text",
  "muted",
  "border",
] as const satisfies readonly (
  keyof NonNullable<SiteBuilderDesign["colors"]>
)[];

function colorPickerValue(value: string): string {
  return /^#[\da-f]{6}$/iu.test(value) ? value : "#000000";
}

function ThemeSection({
  children,
  labels,
  section,
}: {
  children: React.ReactNode;
  labels: BuilderAdvancedThemeInspectorLabels;
  section: string;
}) {
  return (
    <section
      className="space-y-3"
      data-builder-theme-section={section}
    >
      <h3 className="text-[13px] font-semibold text-text">
        {labels.sectionLabel(section)}
      </h3>
      {children}
    </section>
  );
}

function TextField({
  label,
  onBlur,
  onChange,
  value,
}: {
  label: string;
  onBlur: () => void;
  onChange: (value: string) => void;
  value: string;
}) {
  return (
    <label className="grid gap-1.5 text-xs text-text">
      <span>{label}</span>
      <input
        type="text"
        className="h-9 rounded border border-border bg-surface px-2.5 text-xs text-text outline-none focus:border-primary"
        value={value}
        onBlur={onBlur}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

export function BuilderAdvancedThemeInspector({
  config,
  context,
  labels,
}: {
  config: BuilderAdvancedThemeInspectorConfig;
  context: BuilderStudioThemeInspectorContext;
  labels: BuilderAdvancedThemeInspectorLabels;
}) {
  const {
    design,
    finishDesignEdit,
    updateDesign,
  } = context;
  const colors = design.colors ?? {};
  const typography = design.typography ?? {};
  const layout = design.layout ?? {};
  const components = (
    design.components ?? {}
  ) as Record<string, string | undefined>;
  const patchSection = (
    section: "colors" | "typography" | "layout" | "components",
    key: string,
    value: string,
  ) => {
    updateDesign({
      ...design,
      [section]: {
        ...(design[section] ?? {}),
        [key]: value,
      },
    }, `theme:${section}:${key}`);
  };

  return (
    <div
      className="space-y-3"
      data-builder-advanced-theme-inspector="true"
    >
      {config.palettes.length > 0 ? (
        <ThemeSection labels={labels} section="palettes">
          <p className="text-xs leading-5 text-text-secondary">
            {labels.paletteDescription}
          </p>
          <div className="grid gap-2">
            {config.palettes.map((palette) => {
              const active = colorKeys.every((key) => (
                !palette.colors[key]
                || palette.colors[key] === colors[key]
              ));

              return (
                <button
                  type="button"
                  aria-pressed={active}
                  className={[
                    "rounded-md border p-2.5 text-left transition-colors",
                    active
                      ? "border-primary bg-primary/10"
                      : "border-border bg-surface hover:border-primary/55",
                  ].join(" ")}
                  data-builder-theme-palette={palette.key}
                  key={palette.key}
                  onClick={() => {
                    updateDesign({
                      ...design,
                      colors: {
                        ...colors,
                        ...palette.colors,
                      },
                    }, "theme:palette");
                    finishDesignEdit();
                  }}
                >
                  <span className="block text-xs font-semibold text-text">
                    {labels.paletteLabel(palette.key)}
                  </span>
                  <span className="mt-2 flex overflow-hidden rounded border border-border">
                    {colorKeys.map((key) => (
                      <span
                        aria-hidden="true"
                        className="h-5 flex-1"
                        key={key}
                        style={{
                          backgroundColor: palette.colors[key]
                            ?? "transparent",
                        }}
                      />
                    ))}
                  </span>
                </button>
              );
            })}
          </div>
        </ThemeSection>
      ) : null}

      <ThemeSection labels={labels} section="colors">
        <div className="grid gap-3">
          {colorKeys.map((key) => {
            const value = colors[key] ?? "";

            return (
              <label
                className="grid gap-1.5 text-xs text-text"
                data-builder-theme-field={`colors.${key}`}
                key={key}
              >
                <span>{labels.fieldLabel("colors", key)}</span>
                <span className="flex items-center gap-2">
                  <input
                    type="color"
                    aria-label={labels.fieldLabel("colors", key)}
                    className="h-9 w-11 shrink-0 rounded border border-border bg-surface p-1"
                    value={colorPickerValue(value)}
                    onBlur={finishDesignEdit}
                    onChange={(event) => {
                      patchSection("colors", key, event.target.value);
                    }}
                  />
                  <input
                    type="text"
                    aria-label={labels.fieldLabel("colors", key)}
                    className="h-9 min-w-0 flex-1 rounded border border-border bg-surface px-2.5 text-xs text-text outline-none focus:border-primary"
                    value={value}
                    onBlur={finishDesignEdit}
                    onChange={(event) => {
                      patchSection("colors", key, event.target.value);
                    }}
                  />
                </span>
              </label>
            );
          })}
        </div>
      </ThemeSection>

      <ThemeSection labels={labels} section="typography">
        {(["heading", "body"] as const).map((key) => {
          const value = typography[key] ?? "";
          const options = builderSelectOptions(
            value,
            config.fontOptions,
          );

          return (
            <label
              className="grid gap-1.5 text-xs text-text"
              data-builder-theme-field={`typography.${key}`}
              key={key}
            >
              <span>{labels.fieldLabel("typography", key)}</span>
              <select
                className="h-9 rounded border border-border bg-surface px-2.5 text-xs text-text outline-none focus:border-primary"
                value={value}
                onBlur={finishDesignEdit}
                onChange={(event) => {
                  patchSection("typography", key, event.target.value);
                }}
              >
                {options.map((option) => (
                  <option key={option} value={option}>
                    {labels.optionLabel("typography", key, option)}
                  </option>
                ))}
              </select>
            </label>
          );
        })}
      </ThemeSection>

      <ThemeSection labels={labels} section="layout">
        <TextField
          label={labels.fieldLabel("layout", "container")}
          onBlur={finishDesignEdit}
          onChange={(value) => patchSection("layout", "container", value)}
          value={layout.container ?? ""}
        />
        <label className="grid gap-1.5 text-xs text-text">
          <span>{labels.fieldLabel("layout", "sectionSpacing")}</span>
          <select
            className="h-9 rounded border border-border bg-surface px-2.5 text-xs text-text outline-none focus:border-primary"
            value={layout.sectionSpacing ?? ""}
            onBlur={finishDesignEdit}
            onChange={(event) => {
              patchSection("layout", "sectionSpacing", event.target.value);
            }}
          >
            {builderSelectOptions(
              layout.sectionSpacing ?? "",
              config.sectionSpacingOptions,
            ).map((option) => (
              <option key={option} value={option}>
                {labels.optionLabel("layout", "sectionSpacing", option)}
              </option>
            ))}
          </select>
        </label>
        <TextField
          label={labels.fieldLabel("layout", "radius")}
          onBlur={finishDesignEdit}
          onChange={(value) => patchSection("layout", "radius", value)}
          value={layout.radius ?? ""}
        />
      </ThemeSection>

      <ThemeSection labels={labels} section="components">
        {config.componentFields.map((field) => {
          const value = components[field.key] ?? "";
          const options = builderSelectOptions(value, field.options);

          return (
            <label
              className="grid gap-1.5 text-xs text-text"
              data-builder-theme-field={`components.${field.key}`}
              key={field.key}
            >
              <span>{labels.fieldLabel("components", field.key)}</span>
              <select
                className="h-9 rounded border border-border bg-surface px-2.5 text-xs text-text outline-none focus:border-primary"
                value={value}
                onBlur={finishDesignEdit}
                onChange={(event) => {
                  patchSection("components", field.key, event.target.value);
                }}
              >
                {options.map((option) => (
                  <option key={option} value={option}>
                    {labels.optionLabel("components", field.key, option)}
                  </option>
                ))}
              </select>
            </label>
          );
        })}
      </ThemeSection>
    </div>
  );
}

export const BUILDER_THEME_COLOR_KEYS = colorKeys;

export function builderThemeColorPreviewStyle(
  colors: NonNullable<SiteBuilderDesign["colors"]>,
): CSSProperties {
  return {
    background: `linear-gradient(135deg, ${colors.background ?? "#fff"}, ${colors.surface ?? "#fff"} 52%, ${colors.primary ?? "#000"})`,
  };
}
